import { FilterMatchMode } from "@primevue/core/api";
import { helpers } from "@vuelidate/validators";
import { useToast } from "primevue/usetoast";
import { computed, watch } from "vue";
import { useStore } from "vuex";

export const createToast = (toast, severity, summary, detail, life = 5000) => {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: life,
  });
};

export const mergeIds = (arr) => {
  let idsObjArr;
  arr.value.forEach(
    (user) => (idsObjArr = idsObjArr ? [...idsObjArr, user.id] : [user.id])
  );
  return idsObjArr.toString();
};

// VALIDATORS AND FORMS UTILITIES
export let generatePassword = () => {
  const chars = [
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
    "0123456789",
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
    "abcdefghijklmnopqrstuvwxyz",
    "ABCDEFGHIJKLMNOPQRSTUVWXY",
  ];
  return [3, 3, 2, 1, 1]
    .map((len, i) =>
      Array(len)
        .fill(chars[i])
        .map((x) => x[Math.floor(Math.random() * x.length)])
        .join("")
    )
    .concat()
    .join("")
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("")
    .split("0x")
    .join("");
};

export const loginKeypress = (e) => {
  if (/[^a-zA-Z0-9.@_-]/g.test(e.key)) {
    e.preventDefault();
  }
};

export const nameKeypress = (e) => {
  if (/[^a-zA-Z]/g.test(e.key)) {
    e.preventDefault();
  }
};

export const emailKeypress = (e) => {
  if (/[^a-zA-Z0-9.@_-]/g.test(e.key)) {
    e.preventDefault();
  }
};

const regExLogin = /[a-zA-Z0-9@_.~-]/;
const regExName = /[a-zA-Z~]/;
const regExPass =
  "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)[a-zA-Z\\d@#$%&-_*+=|{}:;!.?()\\[\\]]{8,}$";
//eslint-disable-next-line
const regExEmail = /^[a-zA-Z0-9_.~-]+@[a-zA-Z0-9_.~-]+\.[a-zA-Z0-9_.~-]+$/;

export const validationRegExLogin = (value) => value.search(regExLogin) >= 0;
export const validationRegExName = (value) => value.search(regExName) >= 0;
export const validationRegExEmail = (value) => value.search(regExEmail) >= 0;

export const validationRegExPass = (value) => {
  if (!helpers.req(value)) {
    return true;
  }
  const match = value.search(regExPass);
  return match >= 0;
};

// eslint-disable-next-line no-unused-vars
export const validationDuplicateEmail = (errorMsg) => (value) => {
  const valid =
    errorMsg.value === "The user data.email has already been taken." ? -1 : 0;
  return valid >= 0;
};

// eslint-disable-next-line no-unused-vars
export const validationFromAPI = (errorMsg, type) => (value) => {
  let valid;
  switch (type) {
    case "email":
      valid = errorMsg.value.includes("email") ? -1 : 0;
      break;
    case "password":
      valid = errorMsg.value.includes("password") ? -1 : 0;
      break;
  }
  return valid >= 0;
};

// eslint-disable-next-line no-unused-vars
export const validationAuthEmc = (errorMsg) => (value) => {
  const valid =
    errorMsg.value.includes("invalid") ||
    errorMsg.value.includes("failed") ||
    errorMsg.value.includes("Incorrect")
      ? -1
      : 0;
  return valid >= 0;
};

export const encrypt = (msg) => (msg ? btoa(msg) : null);

export const decrypt = (data) => (data ? atob(data) : null);

export const userHasAny = (permissions) => {
  const store = useStore();
  let isAdmin = computed(() => store.getters["auth/userIsAdmin"]);
  if (isAdmin.value) {
    return true;
  }
  let perm = computed(() => store.getters["auth/permissions"]);
  if (perm.value === null || typeof perm.value === "undefined") {
    return false;
  }
  if (permissions === null || typeof permissions === "undefined") {
    return false;
  }
  for (let j = 0; j < permissions.length; j++) {
    if (perm.value.map((x) => x.key).includes(permissions[j])) {
      return true;
    }
  }
  return false;
};

export const userHasAll = (permissions) => {
  const store = useStore();
  let isAdmin = computed(() => store.getters["auth/userIsAdmin"]);
  if (isAdmin.value) {
    return true;
  }
  let perm = computed(() => store.getters["auth/permissions"]);
  if (perm.value === null || typeof perm.value === "undefined") {
    return false;
  }
  if (permissions === null || typeof permissions === "undefined") {
    return false;
  }
  for (let j = 0; j < permissions.length; j++) {
    if (!perm.value.map((x) => x.key).includes(permissions[j])) {
      return false;
    }
  }
  return true;
};

export const copyToClipboard = (value) => {
  if (
    navigator?.clipboard?.writeText &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    return navigator.clipboard.writeText(value);
  }

  return Promise.reject("The Clipboard API is not available.");
};

export const copyValue = async (value, event) => {
  try {
    const orgColor = event.target.style.color;
    await copyToClipboard(value);
    event.target.style.color = "green";
    setTimeout(() => (event.target.style.color = orgColor), 600);
  } catch (error) {
    console.error(error);
  }
};

export const setDatatableFilters = (
  filterData,
  filters,
  filterDisplay = "menu"
) => {
  Object.entries(filterData).forEach(([key, value]) => {
    if (key in filters) {
      const constraint =
        filterDisplay === "menu" ? filters[key].constraints[0] : filters[key];
      constraint.value =
        constraint.matchMode === FilterMatchMode.IN ||
        constraint.matchMode === "inWithEmpty"
          ? [value]
          : value;
    }
  });
};

export const generateSortObjectsFunc = (
  name,
  { caseSensitive = true } = {}
) => {
  return (a, b) => {
    const valueA = caseSensitive
      ? a?.[name]?.toString()?.toLowerCase()
      : a?.[name];
    const valueB = caseSensitive
      ? b?.[name]?.toString()?.toLowerCase()
      : b?.[name];
    if (valueA > valueB) return 1;
    if (valueA < valueB) return -1;
    return 0;
  };
};

export const isObjectsEqual = (a, b) => {
  if (a === b || (a == null && b == null)) return true;
  if (typeof a !== typeof b || (a == null && b) || (b == null && a))
    return false;
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length === keysB.length)
      return keysA.every((key) => {
        const aItem = a[key];
        const bItem = b[key];
        if (typeof aItem === "object" && typeof bItem === "object")
          return isObjectsEqual(aItem, bItem);
        return a[key] === b[key];
      });
  }
  return false;
};

export const isObjectEmpty = (a) => Object.keys(a).length === 0;

export const watchToast = (toastData, options = {}) => {
  const toast = useToast();
  watch(
    toastData,
    (newToastData) => {
      createToast(
        toast,
        newToastData.type,
        newToastData.title,
        newToastData.message
      );
    },
    options
  );
};

export const nl2br = (text) =>
  typeof text === "string" ? text.replace(/\r\n|\r|\n/g, "<br>") : text;

export const objectItemRef = (item, name) => {
  return computed({
    get() {
      return item.value[name];
    },
    set(newValue) {
      item.value[name] = newValue;
    },
  });
};

export const chunkArray = (array, size) => {
  const result = [];
  const copy = [...array];

  while (copy.length > 0) {
    result.push(copy.splice(0, size));
  }
  return result;
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const generateRandomString = (length) => {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  const buffer = new Uint32Array(length);
  crypto.getRandomValues(buffer);

  return Array.from({ length })
    .map((_x, i) => {
      const index = buffer[i] % charset.length;
      return charset.charAt(index);
    })
    .join("");
};

export const undot = (items) => {
  let result = {};

  for (const key of Object.keys(items)) {
    if (key.indexOf(".") !== -1) {
      const obj = result;

      key.split(".").reduce((acc, current, index, array) => {
        acc[current] ??= {};

        if (index === array.length - 1) {
          acc[current] =
            items[key] != null ? structuredClone(items[key]) : null;
        }

        return acc[current];
      }, obj);

      result = { ...result, ...obj };
    } else {
      result[key] = items[key] != null ? structuredClone(items[key]) : null;
    }
  }
  return result;
};
